import { Service, inject } from "@angular/core";
import { LocationTypeEnum } from "app/shared/enums/location-type.enum";
import { AgencyDto } from "app/shared/models/agency.model";
import { HttpBasicRequest } from "app/shared/models/http-basic-request.model";
import {
  OrderItem,
  OrderItemBulkInsertItem,
  OrderItemBulkUpdateItem,
  OrderItemsBulkRequestBody,
} from "app/shared/models/order-item.model";
import { OrderValidationResponseModel } from "app/shared/models/order-validation.model";
import { PagedResponse } from "app/shared/models/paged-response.model";
import { BasicHttpClientService } from "app/shared/services/basic-http-client.service";
import { UtilityFunctions } from "app/shared/utilities/functions.models";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";

import { LocationFullDTO } from "../../../../shared/models/location.model";
import { OrderVisibilityTypeEnum } from "../../enum/order-visibility-type.enum.model";
import { ValidityScopeTypeEnum } from "../../enum/validity-scope-type.enum.model";
import { NewOrderXlsxVm } from "../../models/back-office-admin-common/new-order.models";
import { OrderOperationLogDto } from "../../models/back-office-admin-common/operation-logs.models";
import { OrderItemCommercialDiscountSet } from "../../models/back-office-admin-common/commercial-discount-set.model";
import {
  OrderAssortmentGraphDTO,
  OrderDetailInfo,
  OrderDTO,
  OrderErpServiceGetDto,
  OrderErpServiceInsertDto,
  OrderErpServiceItemGetDto,
  OrderErpServiceUpdateDto,
  OrderHeaderUpdateDTO,
  OrderImportReportDto,
  OrderStatDTO,
  OrderVM,
} from "../../models/back-office-admin-common/order.model";
import {
  VoucherDto,
  VoucherVM,
} from "../../models/back-office-admin-common/vouchers.model";
import {
  BlockOrderTypeEnum,
  CustomerFullDTO,
  CustomerVM,
} from "../../models/customer/customer.model";
import { UserDTO } from "../../models/users/user.model";
import { OrderMapper } from "../../utilities/order-mapper";
import { SessionStore } from "app/shared/state/session.store";
import { ShopContextStore } from "app/shared/state/shop-context.store";
import { CommercialAgreementDTO } from "../../models/commercial-agreement/commercial-agreement.models";
import {
  OrderUploadDetailResponseModel,
  OrderUploadErrorItemResponseModel,
  OrderUploadResponseModel,
} from "app/shared/models/order-upload-response.model";
import { OrderUploadTypeEnum } from "../../enum/order-upload-type.enum";
import {
  ApplicableCartRuleComputationDto,
  ApplicableCartRuleEvaluationDto,
} from "app/shared/models/cart-rule.model";
import { OrderItemsColumnsEnum } from "../../enum/order-columns.enum.model";
import { CustomDeliveryDateVM } from "app/shared/models/custom-deliveries.models";
import { SalesCampaignCartRuleAssortmentConstraintTypeEnum } from "app/shared/enums/sales-campaign-cart-rule-assortment-constraint-type.enum";
import { Order } from "app/shop/shared/models/order.model";
import { ChannelDTO } from "app/shared/models/channel.model";
import { SalesCampaignVM } from "../../models/settings/sales-campaigns.model";
import { HttpParams } from "@angular/common/http";
import { CartCheckoutModel } from "app/shared/models/cart-checkout.model";
import { OrderBulkOperationsStatusDTO } from "../../models/order/order-bulk-operations-status.model";
import { OrderLabelTypeEnum } from "../../models/back-office-admin-common/order-label-type.enum";
import { TenantSettingType } from "app/shared/models/tenant.model";
import { CustomizationRequestDetailDTO } from "app/shared/models/customization-request.models";
import { OrderStatusTypeEnum } from "app/shared/enums/order-status-type.enum.model";
import { PointOfSaleDto } from "app/shared/models/point-of-sale.model";


export interface GetOrdersByStoreIdParams {
  storeId: number;
  searchString?: string;
  skipCount?: number;
  maxResultCount?: number;
  orderStatus?: number[];
  customerId?: number;
  agencyId?: number;
  sorting?: string;
  visibilityType?: OrderVisibilityTypeEnum;
  pointOfSaleId?: number;
  userId?: number;
  hasNote?: boolean;
  externalOrderId?: string;
  channelId?: number;
  seasonReferenceId?: number;
  voucherId?: number;
  hasCR?: boolean;
  orderLabelId?: OrderLabelTypeEnum;
  customizationRequestId?: number;
}
export interface GetOrdersFilteredForBulkParams {
  storeId: number;
  seasonReferenceIds?: number[];
  customerIds?: number[];
  salesCampaignIds?: number[];
  pointOfSaleIds?: number[];
  orderConfirmationDateFrom?: string;
  orderConfirmationDateTo?: string;
  externalIds?: string[];
}

@Service()
export class OrderService {
    // protected: la sottoclasse OrderServiceShop (shop/shared/services/order.service.ts)
  // legge gli stessi store; due `private` omonimi nella stessa gerarchia sono incompatibili (TS2415).
  protected _session = inject(SessionStore);
  protected _shopContext = inject(ShopContextStore);
  protected _httpClient = inject(BasicHttpClientService);


  private _orderActionSelected = new BehaviorSubject<{
    id: number;
    value: string;
  }>(null);
  public orderActionSelected$ = this._orderActionSelected.asObservable();
  public get orderActionSelected() {
    return this._orderActionSelected.value;
  }
  public set orderActionSelected(action: { id: number; value: string }) {
    this._orderActionSelected.next(action);
  }

  private _orderBulkOperationFile = new BehaviorSubject<File>(null);
  public orderBulkOperationFile$ = this._orderBulkOperationFile.asObservable();
  public get orderBulkOperationFile() {
    return this._orderBulkOperationFile.value;
  }
  public set orderBulkOperationFile(file: File) {
    this._orderBulkOperationFile.next(file);
  }

  private _orderBulkCustomerSelected = new BehaviorSubject<CustomerVM>(null);
  public orderBulkCustomerSelected$ =
    this._orderBulkCustomerSelected.asObservable();
  public get orderBulkCustomerSelected() {
    return this._orderBulkCustomerSelected.value;
  }
  public set orderBulkCustomerSelected(customer: CustomerVM) {
    this._orderBulkCustomerSelected.next(customer);
  }

  public _baseUrl: string = environment.urls.apiUrl.url;

  addOrderFinancialDiscount(
    orderId: number,
    discountPercentage: number,
    loaderId: string,
  ): Observable<any> {
    const url = this._baseUrl + "/orders/" + orderId + "/financial-discount";
    return this._httpClient.post(url, { discountPercentage }, null, loaderId);
  }

  removeOrderFinancialDiscount(
    orderId: number,
    loaderId: string,
  ): Observable<any> {
    const url = this._baseUrl + "/orders/" + orderId + "/financial-discount";
    return this._httpClient.delete(url, null, loaderId);
  }

  manageOrderItemsCommercialDiscount(
    orderId: number,
    orderItemsToSet: OrderItemCommercialDiscountSet[] = [],
    orderItemIdsToClear: number[] = [],
    loaderId: string,
  ): Observable<any> {
    const url =
      this._baseUrl + "/orders/" + orderId + "/order-items/commercial-discount";
    return this._httpClient.post(
      url,
      {
        orderItemsToSet: orderItemsToSet || [],
        orderItemIdsToClear: orderItemIdsToClear || [],
      },
      null,
      loaderId,
    );
  }

  getOrdersStats(
    loaderId: string,
    agencyId?: number,
    customerId?: number,
    pointOfSaleId?: number,
  ): Observable<{ preOrder: OrderStatDTO[]; reOrder: OrderStatDTO[] }> {
    const url = this._baseUrl + "/orders/stats";
    const now = new Date();
    const fromDate = new Date(
      new Date(new Date().setMonth(now.getMonth() - 13)).setDate(1),
    );
    const toDate = new Date(now.setDate(now.getDate() + 1));

    const preParams = {
      storeId: this._shopContext.currentShop().id,
      dateFrom: UtilityFunctions.fromDateToDateISOString(fromDate),
      dateTo: UtilityFunctions.fromDateToDateISOString(toDate),
      type: ValidityScopeTypeEnum.PreOrder,
    };

    const reParams = {
      storeId: this._shopContext.currentShop().id,
      dateFrom: UtilityFunctions.fromDateToDateISOString(fromDate),
      dateTo: UtilityFunctions.fromDateToDateISOString(toDate),
      type: ValidityScopeTypeEnum.ReOrder,
    };

    if (agencyId?.toString()) {
      preParams["agencyId"] = agencyId;
      reParams["agencyId"] = agencyId;
    }

    if (customerId?.toString()) {
      preParams["customerId"] = customerId;
      reParams["customerId"] = customerId;
    }

    if (pointOfSaleId?.toString()) {
      preParams["pointOfSaleId"] = pointOfSaleId;
      reParams["pointOfSaleId"] = pointOfSaleId;
    }

    const requests: HttpBasicRequest[] = [
      { url, method: "get", options: { params: preParams } },
      { url, method: "get", options: { params: reParams } },
    ];
    return this._httpClient.forkJoin(requests, loaderId).pipe(
      map((data) => {
        const preOrder: OrderStatDTO[] = data[0] as OrderStatDTO[];
        const reOrder: OrderStatDTO[] = data[1] as OrderStatDTO[];
        return { preOrder, reOrder };
      }),
    );
  }

  sendConfirmationEmailWithCustomMails(
    id: number,
    activateMail: boolean,
    to: string[],
    cc: string[],
    ccn: string[],
    loaderId?: string,
  ): Observable<any> {
    const body = {
      toMailingList: activateMail ? to : null,
      ccMailingList: activateMail ? cc : null,
      bccMailingList: activateMail ? ccn : null,
    };
    const url =
      this._baseUrl + "/orders/" + id.toString() + "/confirmation/re-notify";
    return this._httpClient.post(url, body, null, loaderId);
  }

  getOrdersByStoreId(loaderId: string, filters: GetOrdersByStoreIdParams) {
    const {
      storeId,
      searchString,
      skipCount,
      maxResultCount,
      orderStatus,
      customerId,
      agencyId,
      sorting,
      visibilityType,
      pointOfSaleId,
      userId,
      hasNote,
      externalOrderId,
      channelId,
      seasonReferenceId,
      voucherId,
      hasCR,
      orderLabelId,
      customizationRequestId,
    } = filters;

    let params = {};
    if (storeId) params["storeId"] = storeId;
    visibilityType?.toString()
      ? (params["visibilityType"] = visibilityType)
      : (params["visibilityType"] = OrderVisibilityTypeEnum.all);
    if (skipCount >= 0 && skipCount !== null)
      params["skipCount"] = skipCount.toString();
    if (maxResultCount >= 0 && maxResultCount !== null)
      params["maxResultCount"] = maxResultCount.toString();
    if (searchString) params["searchString"] = searchString;
    if (orderStatus) params["orderStatusIds"] = orderStatus;
    if (customerId) params["customerId"] = customerId;
    if (agencyId) params["agencyId"] = agencyId;
    if (pointOfSaleId) params["pointOfSaleId"] = pointOfSaleId;
    if (userId) params["userId"] = userId;
    if (hasNote) params["hasNote"] = true;
    if (sorting) params["sorting"] = sorting;
    if (externalOrderId) params["externalId"] = externalOrderId;
    if (channelId?.toString()) params["channelId"] = channelId;
    if (seasonReferenceId) params["seasonReferenceId"] = seasonReferenceId;
    if (voucherId) params["voucherId"] = voucherId;
    if (hasCR) params["hasCR"] = hasCR;
    if (orderLabelId) params["orderLabelId"] = orderLabelId;
    if (customizationRequestId)
      params["customizationRequestId"] = customizationRequestId;
    return this._httpClient.get<PagedResponse<OrderDTO>>(
      this._baseUrl + "/orders",
      { params },
      loaderId,
    );
  }

  getOrderDetail(loaderId: string, orderId: number): Observable<OrderVM> {
    return this._httpClient
      .get<OrderDTO>(this._baseUrl + `/orders/${orderId}`, {}, loaderId)
      .pipe(map((orderDetail) => OrderMapper.OrderVm(orderDetail)));
  }

  /**
   * GET /orders/by-product | /orders/by-delivery-date — il raggruppamento del tab Order items,
   * deciso dal BE (commit BE `01a0d5d9`): una riga per (ordine, chiave), con `orderItems`
   * ristretto al gruppo e, su by-delivery-date, le sole `deliveries` di quella data.
   * `by-product` raggruppa per prodotto E colore, la stessa chiave della griglia.
   *
   * Col filtro `OrderId` la risposta è il SOLO ordine chiesto, quindi una riga per gruppo;
   * `MaxResultCount=0` = illimitato, i gruppi di un ordine sono pochi e il paging non serve.
   * Il filter su `row.id` è la difesa pre-deploy: un BE senza il filtro `OrderId` lo ignora e
   * risponderebbe con le righe di TUTTI gli ordini visibili.
   *
   * Non chiede `IncludeVariantDetails`: al chiamante servono solo gli id degli item e le loro
   * consegne, perché gli oggetti da mostrare sono già quelli di `GET /orders/{id}`.
   */
  getOrderItemGroups(
    loaderId: string,
    orderId: number,
    groupBy: "product" | "delivery-date",
  ): Observable<OrderDTO[]> {
    const endpoint = groupBy === "product" ? "by-product" : "by-delivery-date";
    // `orderId` arriva dai queryParams della rotta: e' dichiarato number ma a runtime e' una
    // stringa, e il confronto stretto con `row.id` scarterebbe ogni riga.
    const id = Number(orderId);
    const params = UtilityFunctions.buildParams(
      ["OrderId", id],
      ["VisibilityType", OrderVisibilityTypeEnum.all],
      ["MaxResultCount", 0],
    );
    return this._httpClient
      .get<PagedResponse<OrderDTO>>(
        this._baseUrl + `/orders/${endpoint}`,
        { params },
        loaderId,
      )
      .pipe(map((res) => (res.items ?? []).filter((row) => row.id === id)));
  }

  getDataForAssortmentGraphs(loaderId: string, orderId: number) {
    return this._httpClient.get<OrderAssortmentGraphDTO[]>(
      this._baseUrl + `/orders/${orderId}/assortments-graph`,
      {},
      loaderId,
    );
  }

  getOrderDetailInfo(
    loaderId: string,
    orderDetail: OrderVM,
  ): Observable<OrderDetailInfo> {
    const supportsOrderBulk = this._session
      .userInfo()
      .tenant?.settings?.find(
        (x) => x.type === TenantSettingType.EnabledOrdesBulkOperations,
      )?.value;
    const result = new Subject<OrderDetailInfo>();
    let agencyIndx = null;
    let oldAgencyIndx = null;
    let oldLocIndx = null;
    let crIndx = null;
    let orderBulkIndx = null;
    let posIndx: number | null = null;
    let request: HttpBasicRequest[] = [
      {
        url: this._baseUrl + `/customers/${orderDetail.customerId}`,
        method: "get",
      },
      { url: this._baseUrl + `/users/${orderDetail.userId}`, method: "get" },
      {
        url: this._baseUrl + `/locations/${orderDetail.shippingLocationId}`,
        method: "get",
      },
    ];

    if (supportsOrderBulk) {
      request.push({
        url: this._baseUrl + `/orders/${orderDetail.id}/bulk-operations-status`,
        method: "get",
      });
      orderBulkIndx = request.length - 1;
    }

    if (orderDetail.agencyId) {
      request.push({
        url: this._baseUrl + `/agencies/${orderDetail.agencyId}`,
        method: "get",
      });
      agencyIndx = request.length - 1;
    }

    if (
      orderDetail.confirmationAgencyId &&
      orderDetail.confirmationAgencyId != orderDetail.agencyId
    ) {
      request.push({
        url: this._baseUrl + `/agencies/${orderDetail.confirmationAgencyId}`,
        method: "get",
      });
      oldAgencyIndx = request.length - 1;
    }

    if (
      orderDetail.confirmationShippingLocationId &&
      orderDetail.confirmationShippingLocationId !=
        orderDetail.shippingLocationId
    ) {
      request.push({
        url:
          this._baseUrl +
          `/locations/${orderDetail.confirmationShippingLocationId}`,
        method: "get",
      });
      oldLocIndx = request.length - 1;
    }

    if (orderDetail.customizationRequestId) {
      request.push({
        url:
          this._baseUrl +
          `/customization-requests/${orderDetail.customizationRequestId}/detail`,
        method: "get",
      });
      crIndx = request.length - 1;
    }

    if (orderDetail.pointOfSaleId) {
      request.push({
        url: this._baseUrl + `/point-of-sales/${orderDetail.pointOfSaleId}`,
        method: "get",
      });
      posIndx = request.length - 1;
    }

    this._httpClient.forkJoin<any>(request, loaderId).subscribe((res) => {
      const customer: CustomerFullDTO = res[0];
      const user: UserDTO = res[1];
      const location: LocationFullDTO = res[2];
      const bulkOperationsStatus: OrderBulkOperationsStatusDTO = orderBulkIndx
        ? res[orderBulkIndx]
        : null;
      const agency: AgencyDto = agencyIndx ? res[agencyIndx] || null : null;
      const agencyOld: AgencyDto = oldAgencyIndx
        ? res[oldAgencyIndx] || null
        : agencyIndx
          ? res[agencyIndx]
          : null;
      const locationOld: LocationFullDTO = oldLocIndx
        ? res[oldLocIndx] || null
        : null;
      const crDetails: CustomizationRequestDetailDTO = crIndx
        ? res[crIndx] || null
        : null;
      const posDto: PointOfSaleDto = posIndx !== null ? res[posIndx] : null;
      if (res) {
        const pos = orderDetail.pointOfSale;
        const customerLoc = customer.customerLocations?.find(
          (element) =>
            element.locationType === LocationTypeEnum.BillingLocation,
        );
        const agencyCode =
          agency?.storeActivations.find(
            (x) => x.storeId === orderDetail.storeId,
          )?.agencyCode ?? "";
        let orderDetailInfo: OrderDetailInfo = {
          bulkOperationsStatus,
          agentEmail: "",
          agentName: "",
          customerLocation: customerLoc
            ? [
                customerLoc?.location?.address1,
                customerLoc?.location?.city,
                customerLoc?.location?.province,
                customerLoc?.location?.country?.name,
              ]
                .filter(Boolean)
                .join(", ")
            : "",
          customerName:
            (customer.externalId ? customer.externalId + " - " : "") +
            customer?.name,
          posLocation: pos
            ? `${pos?.name} - ${pos?.location?.address1}, ${pos?.location?.city}, ${pos?.location?.province}, ${pos?.location?.country?.name}`
            : "",
          posName: pos
            ? `${pos?.name}, ${pos?.location?.address1}, ${pos?.location?.city}`
            : "",
          shipAddressLocation: location
            ? [
                location?.address1,
                location?.city,
                location?.province,
                location?.country?.name,
              ]
                .filter(Boolean)
                .join(", ")
            : "",
          userEmail: user?.email,
          userName: user?.name,
          agencyCode: agencyCode,
          agencyName: agency?.name ?? "",
          externalId: orderDetail.externalId,
          posStatus: posDto?.storePointOfSales?.find(
            (element) => element.store.id === orderDetail.storeId,
          )?.checkInStatus,
          customerStatus: customer.checkInStatus,
          paymentTerm: orderDetail.paymentTerm,
          isCustomerCommercialBlocked:
            customer.blockOrderType !== BlockOrderTypeEnum.Open,
          blockOrderConfirmationEmailByDefault:
            !!customer.blockOrderConfirmationEmailByDefault,
          confirmationAgencyCode: agencyOld?.storeActivations?.find(
            (s) => s.storeId === orderDetail.storeId,
          )?.agencyCode,
          confirmationAgencyName: agencyOld?.name,
          confirmationAgencyId: orderDetail.confirmationAgencyId,
          confirmationShippingLocationId:
            orderDetail.confirmationShippingLocationId,
          confirmationShipAddressLocation: locationOld
            ? `${locationOld?.address1}, ${locationOld?.city}, ${locationOld?.province}, ${locationOld?.country?.name}`
            : "",
          storeName: orderDetail?.store?.name,
          cancellationReason: orderDetail?.cancellationReason,
          crDetails: crDetails,
          centralPaymentNumberFullName:
            orderDetail.orderStatus === OrderStatusTypeEnum.Draft
              ? customer.centralPaymentParentCustomerExternalId +
                " - " +
                customer.centralPaymentParentCustomerName
              : orderDetail.centralPaymentNumberFullName,
          invoicingNumberFullName:
            orderDetail.orderStatus === OrderStatusTypeEnum.Draft
              ? customer.invoicingParentCustomerExternalId +
                " - " +
                customer.invoicingParentCustomerName
              : orderDetail.invoicingNumberFullName,
          invoicingParentCustomerId:
            orderDetail.orderStatus === OrderStatusTypeEnum.Draft
              ? customer.invoicingParentCustomerId
              : orderDetail.invoicingParentCustomerId,
        };
        result.next(orderDetailInfo);
      }
    });
    return result;
  }

  getOrderValidation(loaderId: string, orderId: number) {
    return this._httpClient.get<OrderValidationResponseModel>(
      this._baseUrl + `/orders/${orderId}/order-validation`,
      {},
      loaderId,
    );
  }

  getUsersForSendEmail(loaderId: string, customerId: number) {
    return this._httpClient.get<PagedResponse<UserDTO>>(
      this._baseUrl + `/users`,
      {
        params: {
          customerId: customerId.toString(),
        },
      },
      loaderId,
    );
  }

  updateCommercialAgreement(
    loaderId: string,
    commercialAgreementId: number,
    orderId: number,
    orderDetail: any,
    removeCommercialAgreement = false,
  ) {
    const commercialId = removeCommercialAgreement
      ? null
      : commercialAgreementId;
    return this.updateOrder(
      orderId,
      { ...orderDetail, commercialAgreementId: commercialId },
      loaderId,
    );
  }

  updateOrder(
    orderId: number,
    orderDetail: OrderVM | Order,
    loaderId?: string,
    voucherId?: number,
    skipVisibilityAndQuantityValidation?: boolean,
  ) {
    if (orderDetail.skipAutomaticDiscounts !== null)
      orderDetail["skipAutomaticDiscounts"] =
        orderDetail.skipAutomaticDiscounts;
    if (orderDetail.skipStoreChannelDiscounts !== null)
      orderDetail["skipStoreChannelDiscounts"] =
        orderDetail.skipStoreChannelDiscounts;
    if (orderDetail.skipCustomerDiscounts !== null)
      orderDetail["skipCustomerDiscounts"] = orderDetail.skipCustomerDiscounts;
    if (voucherId) orderDetail["vouchersToApply"] = [voucherId];
    if (skipVisibilityAndQuantityValidation)
      orderDetail["skipVisibilityAndQuantityValidation"] =
        skipVisibilityAndQuantityValidation;
    return this._httpClient.put<OrderDTO>(
      this._baseUrl + `/orders/${orderId}`,
      orderDetail,
      {},
      loaderId,
    );
  }

  updateOrderHeader(
    orderId: number,
    orderHeaderUpdateDTO: OrderHeaderUpdateDTO,
    loaderId: string,
  ) {
    return this._httpClient.post<OrderDTO>(
      this._baseUrl + `/order-bulk-operations/orders/${orderId}/update-header`,
      orderHeaderUpdateDTO,
      {},
      loaderId,
    );
  }

  deleteOrder(loaderId: string, orderId: number) {
    return this._httpClient.delete(
      this._baseUrl + `/orders/${orderId}`,
      {},
      loaderId,
    );
  }

  sendCartByEmail(loaderId: string, emails: string[], subject, message) {
    const sendEmailsRequest: HttpBasicRequest[] = OrderMapper.sendEmailsRequest(
      emails,
      subject,
      message,
      this._baseUrl,
    );
    this._httpClient
      .forkJoin(sendEmailsRequest, loaderId)
      .subscribe((res) => {});
  }

  exportXlsx(
    loaderId: string,
    orderId: number,
    orderCustomColumnsList: OrderItemsColumnsEnum[] = [],
  ): Observable<Blob> {
    // const params = orderItemsColumnsIds;
    const params = { orderCustomColumnsList };
    return this._httpClient
      .getBlob(`${this._baseUrl}/orders/${orderId}/xlsx`, { params }, loaderId)
      .pipe(map((x) => x.blob));
  }

  exportPdf(loaderId: string, orderId: number): Observable<Blob> {
    return this._httpClient
      .getBlob(`${this._baseUrl}/orders/${orderId}/pdf`, {}, loaderId)
      .pipe(map((x) => x.blob));
  }

  exportOrderZip(loaderId: string, orderId: number): Observable<Blob> {
    return this._httpClient
      .getBlob(`${this._baseUrl}/orders/${orderId}/zip`, {}, loaderId)
      .pipe(map((x) => x.blob));
  }

  createOrderFromXlsx(loaderId: string, newOrder: NewOrderXlsxVm) {
    const formData = new FormData();
    formData.append("file", newOrder.fileXlsx);

    const params: any = {
      billingLocationId: newOrder.billingLocationId,
      customerId: newOrder.customer,
      name: newOrder.name,
      internalNote: newOrder.internalNote,
      note: newOrder.note,
      pointOfSaleId: newOrder.pointOfSaleId,
      shippingLocationId: newOrder.shippingLocationId,
      storeId: newOrder.storeId,
      type: newOrder.type,
    };

    return this._httpClient.post<OrderImportReportDto>(
      this._baseUrl + `/orders/create-from-xlsx`,
      formData,
      { params },
      loaderId,
    );
  }

  getApplicableVouchers(loaderId: string, orderId: number) {
    return this._httpClient
      .get<
        VoucherDto[]
      >(this._baseUrl + `/orders/${orderId}/applicable-vouchers`, {}, loaderId)
      .pipe(map((x) => x.map((y) => new VoucherVM(y))));
  }

  insertOrUpdateOrDeleteOrderItems(
    loaderId: string,
    orderItemsToInsert: OrderItem[],
    orderItemsToUpdate: OrderItem[],
    orderItemsToDelete: number[],
    orderId: number,
  ) {
    const body = new OrderItemsBulkRequestBody({
      orderItemsToInsert: orderItemsToInsert?.map(
        (x) =>
          new OrderItemBulkInsertItem({
            discountPercentage: null,
            internalNote: x.internalNote,
            note: x.note,
            quantity: x.quantity,
            requestedDeliveryDate: x.requestedDeliveryDate,
            variantId: x.variantId,
          }),
      ),
      orderItemsToUpdate: orderItemsToUpdate?.map(
        (x) =>
          new OrderItemBulkUpdateItem({
            discountPercentage: null,
            id: x.id,
            internalNote: x.internalNote,
            note: x.note,
            quantity: x.quantity,
            requestedDeliveryDate: x.requestedDeliveryDate,
          }),
      ),
      orderItemIdsToRemove: orderItemsToDelete,
    });
    return this._httpClient.post(
      `${this._baseUrl}/orders/${orderId}/bulk-order-items`,
      body,
      null,
      loaderId,
    );
  }

  getOrderOperationLogs(
    loaderId: string,
    orderId: number,
  ): Observable<OrderOperationLogDto[]> {
    return this._httpClient
      .get<
        OrderOperationLogDto[]
      >(`${this._baseUrl}/orders/${orderId}/erp-operation-logs`, {}, loaderId)
      .pipe(map((x) => x.map((y) => new OrderOperationLogDto(y))));
  }

  getApplicableCommercialAgreements(loaderId: string, orderId: number) {
    return this._httpClient.get<CommercialAgreementDTO[]>(
      `${this._baseUrl}/orders/${orderId}/applicable-agreements`,
      {},
      loaderId,
    );
  }

  getOrderUploads(
    loaderId: string,
    orderId: number,
    maxResultCount: number,
    skipCount: number,
  ): Observable<PagedResponse<OrderUploadResponseModel>> {
    const params = { orderId, maxResultCount, skipCount };
    return this._httpClient.get<PagedResponse<OrderUploadResponseModel>>(
      `${this._baseUrl}/cart-load-operations`,
      { params },
      loaderId,
    );
  }

  getCartUploadTemplate(
    loaderId: string,
    orderId: number,
    operationCodeType: OrderUploadTypeEnum,
  ): Observable<Blob> {
    const params = { operationCodeType };
    return this._httpClient
      .getBlob(
        `${this._baseUrl}/orders/${orderId}/sample-cart-load-xlsx`,
        { params },
        loaderId,
      )
      .pipe(map((x) => x.blob));
  }

  getOrderUploadTemplate(
    loaderId: string,
    orderId: number,
    codeType: OrderUploadTypeEnum,
  ): Observable<Blob> {
    const params = { codeType };
    return this._httpClient
      .getBlob(
        `${this._baseUrl}/orders/${orderId}/update-from-excel`,
        { params },
        loaderId,
      )
      .pipe(map((x) => x.blob));
  }

  getOrderUploadDetails(
    loaderId: string,
    operationId: number,
  ): Observable<OrderUploadDetailResponseModel> {
    const itemsWithIsuuesParams = { maxResultCount: 1000, skipCout: 0 };
    const headers = { skipErrorHandling: "true" };
    const requests: HttpBasicRequest[] = [
      {
        url: `${this._baseUrl}/cart-load-operations/${operationId}`,
        method: "get",
        options: { headers },
      },
      {
        url: `${this._baseUrl}/cart-load-operations/${operationId}/items-with-issues`,
        method: "get",
        options: { headers, params: itemsWithIsuuesParams },
      },
    ];
    return this._httpClient.forkJoin(requests, loaderId).pipe(
      map((data) => {
        const detail = data[0] as OrderUploadDetailResponseModel;
        if (!detail)
          throw new Error(`Cart load operation ${operationId} not found`);
        const issuesResult =
          data[1] as PagedResponse<OrderUploadErrorItemResponseModel>;
        detail.errors = issuesResult?.items || [];
        return detail;
      }),
    );
  }

  uploadFile(
    loaderId: string,
    orderId: number,
    file: File,
    codeType: OrderUploadTypeEnum,
    isOrder: boolean,
  ): Observable<number> {
    let params;
    if (!isOrder) params = { orderId, codeType };
    const body = new FormData();
    body.append("file", file);
    if (isOrder) body.append("codeType", String(codeType));
    return isOrder
      ? this._httpClient.post<number>(
          `${this._baseUrl}/orders/${orderId}/update-from-excel`,
          body,
          { params },
          loaderId,
        )
      : this._httpClient.post<number>(
          `${this._baseUrl}/cart-load-operations`,
          body,
          { params },
          loaderId,
        );
  }

  createOrder(loaderId: string, order: OrderDTO) {
    return this._httpClient.post<OrderDTO>(
      `${this._baseUrl}/orders`,
      order,
      null,
      loaderId,
    );
  }

  getApplicableSalesCampaignsRules(loaderId: string, orderId: number) {
    return this._httpClient.get<ApplicableCartRuleComputationDto>(
      `${this._baseUrl}/orders/${orderId}/applicable-rule`,
      {},
      loaderId,
    );
  }

  evaluateCartSaleCampaignConstraint(loaderId: string, orderId: number) {
    return this._httpClient
      .get<ApplicableCartRuleEvaluationDto>(
        `${this._baseUrl}/orders/${orderId}/applicable-rule/evaluation`,
        {},
        loaderId,
      )
      .pipe(
        map((data) => {
          if (data.evaluationResult?.ruleAssortmentEvaluations) {
            data.evaluationResult.ruleAssortmentEvaluations =
              data.evaluationResult?.ruleAssortmentEvaluations.filter((rs) =>
                rs.constraintEvaluations.some(
                  (x) =>
                    (x.constraintType ===
                      SalesCampaignCartRuleAssortmentConstraintTypeEnum.Quantity &&
                      x.matchedItemIds.length > 0) ||
                    x.constraintType ===
                      SalesCampaignCartRuleAssortmentConstraintTypeEnum.Value,
                ),
              );
          }
          if (!data.evaluationResult?.ruleAssortmentEvaluations?.length)
            data.applicableCartRuleComputationResult.applicableRuleId = null;
          return data;
        }),
      );
  }

  customDeliveryDate(
    loaderId: string,
    deliveries: CustomDeliveryDateVM[],
    orderId,
  ) {
    const requests: HttpBasicRequest[] = deliveries.map((x) => {
      return {
        url: `${this._baseUrl}/orders/${orderId}/order-item/${x.orderItemId}/custom-deliveries`,
        method: "post",
        body: x.deliveries,
      };
    });

    return this._httpClient.forkJoin(requests, loaderId, 50);
  }

  getChannelsAndCustomersForOrdersDownload(loaderId: string, storeId: number) {
    const requests: HttpBasicRequest[] = [
      {
        url: `${this._baseUrl}/channels`,
        method: "get",
        options: { params: { skipCount: 0, maxResultCount: 1000 } },
      },
      {
        url: `${this._baseUrl}/customers`,
        method: "get",
        options: {
          params: {
            skipCount: 0,
            maxResultCount: 1000,
            includeLocation: "false",
            includePointOfSale: "false",
            storeId: storeId,
            includeCustomerVouchers: "false",
          },
        },
      },
      {
        url: `${this._baseUrl}/sales-campaign`,
        method: "get",
        options: { params: { skipCount: 0, maxResultCount: 1000 } },
      },
    ];

    return this._httpClient.forkJoin(requests, loaderId).pipe(
      map((res: any[]) => {
        return {
          channels: res[0].items as ChannelDTO[],
          customers: res[1].items as CustomerVM[],
          salesCampaigns: res[2].items as SalesCampaignVM[],
        };
      }),
    );
  }

  checkoutOrder(
    orderId: number,
    checkoutData: CartCheckoutModel,
    loaderId: string,
  ) {
    const payload = {
      id: orderId,
      ccEmails: checkoutData.ccEmails || [],
      skipVisibilityAndQuantityValidation:
        checkoutData.skipVisibilityAndQuantityValidation || false,
      toEmails: checkoutData.toEmails || [],
      vouchersToApply: checkoutData.vouchersToApply || [],
      termsOfSaleId: checkoutData.termsOfSaleId,
      commercialAgreementId: checkoutData.commercialAgreementId,
      useFixedConfirmEmailRecipients:
        checkoutData.useFixedConfirmEmailRecipients || false,
      attachments: checkoutData.attachments,
      courierId: checkoutData.courierId,
    };
    return this._httpClient.put(
      `${this._baseUrl}/orders/${orderId}/checkout`,
      payload,
      { headers: { skipErrorHandling: "true" } },
      loaderId,
    );
  }

  getOrdersFilteredForBulk(filters: GetOrdersFilteredForBulkParams, loaderId?: string): Observable<any> {
    const {
      storeId,
      seasonReferenceIds,
      customerIds,
      salesCampaignIds,
      pointOfSaleIds,
      orderConfirmationDateFrom,
      orderConfirmationDateTo,
      externalIds,
    } = filters;

    let params = new HttpParams().set("storeId", storeId.toString());
    if (seasonReferenceIds) {
      seasonReferenceIds.forEach(
        (id) => (params = params.append("seasonReferenceIds", id.toString())),
      );
    }
    if (customerIds) {
      customerIds.forEach(
        (id) => (params = params.append("customerIds", id.toString())),
      );
    }
    if (salesCampaignIds) {
      salesCampaignIds.forEach(
        (id) => (params = params.append("salesCampaignIds", id.toString())),
      );
    }
    if (pointOfSaleIds) {
      pointOfSaleIds.forEach(
        (id) => (params = params.append("pointOfSaleIds", id.toString())),
      );
    }
    if (externalIds) {
      externalIds.forEach((id) => (params = params.append("externalIds", id)));
    }
    if (orderConfirmationDateFrom) {
      params = params.append(
        "orderConfirmationDateFrom",
        orderConfirmationDateFrom,
      );
    }
    if (orderConfirmationDateTo) {
      params = params.append(
        "orderConfirmationDateTo",
        orderConfirmationDateTo,
      );
    }
    return this._httpClient.get<any>(
      `${this._baseUrl}/orders/select-for-bulk`,
      { params },
      loaderId,
    );
  }
  resendOrderConfirmationToCustomer(orderId: number, loaderId: string) {
    return this._httpClient.post(
      `${this._baseUrl}/orders/${orderId}/confirmation/re-notify-to-customer`,
      null,
      {},
      loaderId,
    );
  }

  createOrderErpService(
    loaderId: string,
    orderId: number,
    erpService: OrderErpServiceInsertDto,
  ) {
    return this._httpClient.post<OrderErpServiceGetDto>(
      `${this._baseUrl}/orders/${orderId}/erp-service-items`,
      erpService,
      null,
      loaderId,
    );
  }

  updateOrderErpService(
    loaderId: string,
    orderId: number,
    erpServiceExternalId: string,
    erpDiscountedTotalPrice: number,
  ) {
    return this._httpClient.put<OrderErpServiceGetDto>(
      `${this._baseUrl}/orders/${orderId}/erp-service-items/${erpServiceExternalId}`,
      new OrderErpServiceUpdateDto({
        erpDiscountedTotalPrice,
      }),
      null,
      loaderId,
    );
  }

  createUpdateDeleteOrderErpServices(
    loaderId: string,
    orderId: number,
    items: OrderErpServiceItemGetDto[],
  ) {
    let requests: HttpBasicRequest[] =
      items
        ?.filter((x) => !x.id)
        .map((el) => {
          return new HttpBasicRequest({
            method: "post",
            url: `${this._baseUrl}/orders/${orderId}/erp-service-items`,
            body: new OrderErpServiceInsertDto({
              serviceExternalId:
                el.erpService.externalId || UtilityFunctions.newGuid(),
              erpDiscountedTotalPrice: el.erpDiscountedTotalPrice,
            }),
          });
        }) || [];
    requests = requests.concat(
      items
        ?.filter(
          (x) =>
            !!x.id &&
            x.erpDiscountedTotalPrice !== null &&
            x.erpDiscountedTotalPrice !== undefined,
        )
        .map((el) => {
          return new HttpBasicRequest({
            method: "put",
            url: `${this._baseUrl}/orders/${orderId}/erp-service-items/${el.erpService.externalId}`,
            body: new OrderErpServiceUpdateDto({
              erpDiscountedTotalPrice: el.erpDiscountedTotalPrice,
            }),
          });
        }) || [],
    );
    requests = requests.concat(
      items
        ?.filter(
          (x) =>
            !!x.id &&
            (x.erpDiscountedTotalPrice === null ||
              x.erpDiscountedTotalPrice === undefined),
        )
        .map((el) => {
          return new HttpBasicRequest({
            method: "delete",
            url: `${this._baseUrl}/orders/${orderId}/erp-service-items/${el.erpService.externalId}`,
          });
        }) || [],
    );
    return this._httpClient.forkJoin(requests, loaderId);
  }
}
